import React, { FC, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { styled } from '@styledComponents';
import { Box } from '@components/Box';

interface TextCarouselProps {
  content: readonly string[];
  style?: React.CSSProperties;
  className?: string;
  /** Duration each phrase is shown (ms) */
  displayDuration?: number;
  /** Duration of slide transition (ms) */
  transitionDuration?: number;
}

const CarouselContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  display: inline-block;
  vertical-align: baseline;
`;

const CarouselTrack = styled.div<{
  $translateY: number;
  $transitionDuration: number;
  $isTransitioning: boolean;
}>`
  display: flex;
  flex-direction: column;
  transform: translateY(${({ $translateY }) => -$translateY}px);
  transition: ${({ $isTransitioning, $transitionDuration }) =>
    $isTransitioning ? `transform ${$transitionDuration}ms cubic-bezier(0.33, 1, 0.68, 1)` : 'none'};
`;

const CarouselItem = styled.div`
  white-space: nowrap;
  text-align: center;
`;

/**
 * TextCarousel - A smooth vertical text carousel animation
 *
 * Cycles through an array of strings with a vertical slide transition.
 * The container automatically sizes to the longest string, keeping
 * the layout stable and visually centered.
 */
const TextCarousel: FC<TextCarouselProps> = ({ content, style, className, displayDuration = 3000, transitionDuration = 600 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [itemHeight, setItemHeight] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const firstItemRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Measure the height of one item for the container
  useLayoutEffect(() => {
    if (firstItemRef.current) {
      const height = firstItemRef.current.offsetHeight;
      setItemHeight(height);
      setIsReady(true);
    }
  }, []);

  // Animation loop
  useEffect(() => {
    if (!isReady) return;

    const startTimer = () => {
      timerRef.current = setTimeout(() => {
        setIsTransitioning(true);
        setCurrentIndex((prev) => prev + 1);
      }, displayDuration);
    };

    startTimer();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, displayDuration, isReady]);

  // Handle loop reset after transition completes
  useEffect(() => {
    if (currentIndex >= content.length && isTransitioning) {
      const resetTimer = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(0);
      }, transitionDuration);

      return () => clearTimeout(resetTimer);
    }
  }, [currentIndex, content.length, isTransitioning, transitionDuration]);

  // Create items array with duplicate first item at end for seamless loop
  const items = [...content, content[0]];

  // Don't render until we've measured
  if (!isReady && typeof window !== 'undefined') {
    // First render - measure pass
    return (
      <CarouselContainer style={style} className={className}>
        <CarouselTrack $translateY={0} $transitionDuration={0} $isTransitioning={false}>
          <CarouselItem ref={firstItemRef}>{content[0]}</CarouselItem>
        </CarouselTrack>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer
      style={{
        ...style,
        height: itemHeight > 0 ? `${itemHeight}px` : undefined,
      }}
      className={className}
    >
      <CarouselTrack $translateY={currentIndex * itemHeight} $transitionDuration={transitionDuration} $isTransitioning={isTransitioning}>
        {items.map((text, index) => (
          <CarouselItem key={`${text}-${index}`} ref={index === 0 ? firstItemRef : undefined}>
            {text}
          </CarouselItem>
        ))}
      </CarouselTrack>
    </CarouselContainer>
  );
};

export default TextCarousel;
